import { useState } from 'react';
import {
    Box,
    Card,
    CardContent,
    CardActions,
    Button,
    Typography,
    TextField,
    InputAdornment,
    CardMedia,
    CircularProgress,
    Alert,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import CloseIcon from '@mui/icons-material/Close';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { useTranslation } from 'react-i18next';
import { useGetAcceleratorListQuery, useInstallAcceleratorMutation, api } from '../../../redux/api';
import { useSelector, useDispatch, useStore } from 'react-redux';
import { treeActions } from '../../../redux/slices/treeSlice';
import { lgDocPath } from '../../../constants/languageDocPaths';
import AcceleratorWorkflowWizard from './AcceleratorWorkflowWizard';

const AcceleratorWizard = ({ handleClose, setIsInstalling, setSnackbar }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const store = useStore();
    const [searchTerm, setSearchTerm] = useState('');
    const [installing, setInstalling] = useState(null);
    const [isInstallingWizard, setIsInstallingWizard] = useState(false);
    const [showConfigDialog, setShowConfigDialog] = useState(false);
    const [showWorkflowWizard, setShowWorkflowWizard] = useState(false);
    const [installationData, setInstallationData] = useState(null);
    const { account, user, baseUrl, lang } = useSelector((state) => state.queryParams);

    // Fetch accelerator list from API
    const { data, isLoading, error } = useGetAcceleratorListQuery({ account, user });
    const [installAccelerator, { isLoading: isInstalling }] = useInstallAcceleratorMutation();
    console.log('Accelerator List Data:', data);
    const accelerators = data?.data?.accelerators || [];

    const filteredAccelerators = accelerators.filter(acc => {
        const matchesSearch = acc.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            acc.description.toLowerCase().includes(searchTerm.toLowerCase());
        return matchesSearch;
    });

    const handleInstall = async (accelerator) => {
        if (!accelerator.package) return;
        
        setInstalling(accelerator.name);
        setIsInstallingWizard(true);
        setIsInstalling?.(true);
        try {
            const response = await installAccelerator({
                account,
                user,
                package: accelerator.package
            }).unwrap();
            
            console.log('Installation response:', response);

            if (response?.current_state === 'ERROR') {
                setIsInstallingWizard(false);
                setIsInstalling?.(false);
                setSnackbar?.({
                    isOpen: true,
                    message: response?.message || t('ACCELERATOR_INSTALL_FAILED'),
                    severity: 'error'
                });
                return;
            }
            
            // If installation successful, select the first workflow (ascending alphabetical) and show config dialog
            if (response?.current_state === 'SUCCESS') {
                const data = response?.data?.data || response?.data || response;
                const workflows = data?.workflows || data?.workflow_templates || [];
                const workflow_group = data?.workflow_group || data?.workflowGroup || data?.workflow_group_name;
                const sortedWorkflows = Array.isArray(workflows)
                    ? [...workflows].sort((a, b) => {
                        const nameA = typeof a === 'string' ? a : (a?.name ?? a?.title ?? '');
                        const nameB = typeof b === 'string' ? b : (b?.name ?? b?.title ?? '');
                        return String(nameA).localeCompare(String(nameB), undefined, { sensitivity: 'base' });
                    })
                    : [];
                const firstWorkflow = sortedWorkflows.length > 0
                    ? (typeof sortedWorkflows[0] === 'string' ? { name: sortedWorkflows[0], title: sortedWorkflows[0] } : sortedWorkflows[0])
                    : null;

                // Fetch workflow group tree so the new group appears
                await dispatch(api.endpoints.getWorkflows.initiate({ account }, { subscribe: false, forceRefetch: true }));

                if (workflow_group && firstWorkflow) {
                    const payload = {
                        workflowGroup: workflow_group,
                        template: firstWorkflow,
                        workflows: workflows
                    };
                    setInstallationData(payload);
                    dispatch(treeActions.setCurrentScreen('workflow'));
                    dispatch(treeActions.setLeftTreeContextActionValue({ screen: 'workflow', data: false }));
                    dispatch(treeActions.setForceTemplate({
                        workflowGroup: workflow_group,
                        template: firstWorkflow,
                        forceSelect: true,
                        screen: 'workflow'
                    }));
                    dispatch(treeActions.setPendingAcceleratorConfigDialog(payload));
                } else {
                    setInstallationData(null);
                }

                setIsInstallingWizard(false);
                setIsInstalling?.(false);
                setSnackbar?.({
                    isOpen: true,
                    message: t('ACCELERATOR_INSTALL_SUCCESS'),
                    severity: 'success'
                });

                // Close Repository so user sees workflow screen; dialog will show there after template loads
                handleClose();
                // If Repository stays open (e.g. handleClose no-op), show dialog here
                setTimeout(() => setShowConfigDialog(true), 100);
            }
        } catch (err) {
            console.error('Installation failed:', err);
            setIsInstallingWizard(false);
            setIsInstalling?.(false);
            // You can add error notification here if needed
        } finally {
            setInstalling(null);
        }
    };

    const handleLearnMore = (accelerator) => {
        // Open readme PDF in new tab
        if (accelerator.readme) {
            window.open(accelerator.readme, '_blank');
        }
    };

    const handleOpenHelp = () => {
        dispatch(treeActions.setShowHelpPanel({
            docType: "ACCELERATOR",
            isOpen: true
        }));
    };

    const handleProceedWithConfig = () => {
        setShowConfigDialog(false);
        if (installationData?.workflowGroup && Array.isArray(installationData.workflows) && installationData.workflows.length > 0) {
            dispatch(treeActions.setLeftTreeContextActionValue({ screen: 'workflow', data: false }));
            const sorted = [...installationData.workflows].sort((a, b) => {
                const nameA = typeof a === 'string' ? a : (a?.name ?? a?.title ?? '');
                const nameB = typeof b === 'string' ? b : (b?.name ?? b?.title ?? '');
                return String(nameA).localeCompare(String(nameB), undefined, { sensitivity: 'base' });
            });
            dispatch(treeActions.setAcceleratorWorkflowQueue({
                workflowGroup: installationData.workflowGroup,
                workflows: sorted,
                currentIndex: 0
            }));
        }
        setTimeout(() => {
            setShowWorkflowWizard(true);
        }, 500);
    };

    const handleSkipConfig = () => {
        setShowConfigDialog(false);
        // Ensure proper selection: first workflow group, first workflow, first item
        if (installationData) {
            const { workflowGroup, template } = installationData;
            
            // Ensure the workflow is selected
            dispatch(treeActions.setCurrentScreen('workflow'));
            dispatch(treeActions.setForceTemplate({
                workflowGroup: workflowGroup,
                template: template,
                forceSelect: true,
                screen: 'workflow'
            }));
            
            // Wait for the tree to load, then select the first item
            let retryCount = 0;
            const maxRetries = 10;
            const selectFirstItem = () => {
                const state = store.getState();
                const rightTree = state.treeData?.workflow?.rightTree;
                const translatedData = rightTree?.tree?.body?.translatedData;
                if (translatedData && translatedData.length > 0) {
                    const firstItem = translatedData[0];
                    dispatch(treeActions.setRightTreeSelectedNode({
                        screen: 'workflow',
                        type: 'body',
                        data: firstItem
                    }));
                    handleClose();
                } else if (retryCount < maxRetries) {
                    // If tree not loaded yet, try again after a short delay
                    retryCount++;
                    setTimeout(selectFirstItem, 200);
                } else {
                    // Max retries reached, just close
                    handleClose();
                }
            };
            
            // Start trying to select first item after a short delay
            setTimeout(selectFirstItem, 500);
        } else {
            handleClose();
        }
    };

    const handleWorkflowWizardClose = () => {
        setShowWorkflowWizard(false);
        handleClose();
    };

    return (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', position: 'relative' }}>
            {/* Installation Loading Overlay */}
            {isInstallingWizard && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 9999,
                        gap: 2
                    }}
                >
                    <CircularProgress size={60} />
                    <Typography variant="h6" sx={{ color: '#666', fontWeight: 500 }}>
                        {t('ACCELERATOR_INSTALLING_TITLE')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {t('ACCELERATOR_PLEASE_WAIT_SETUP')}
                    </Typography>
                </Box>
            )}

            {/* Header Section */}
            <Box sx={{ p: 3, backgroundColor: '#f9f9f9' }}>
                <Typography variant="h5" sx={{ fontWeight: 600, mb: 1.5, color: '#333' }}>
                    {t('ACCELERATOR_TIT_BRIDGEGATE')}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5, lineHeight: 1.6 }}>
                    {t('ACCELERATOR_DESC_PREBUILT')}
                </Typography>
                <TextField
                    fullWidth
                    size="small"
                    placeholder={t('ACCELERATOR_SEARCH_PLACEHOLDER')}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    disabled={isInstallingWizard}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon sx={{ color: '#999' }} />
                            </InputAdornment>
                        ),
                    }}
                    sx={{ 
                        backgroundColor: 'white',
                        '& .MuiOutlinedInput-root': {
                            '& fieldset': {
                                borderColor: '#ddd',
                            },
                        }
                    }}
                />
            </Box>

            {/* Accelerators Grid */}
            <Box sx={{ flexGrow: 1, overflow: 'auto', p: 3, backgroundColor: '#fff' }}>
                {/* Loading State */}
                {isLoading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                        <CircularProgress />
                    </Box>
                )}

                {/* Error State */}
                {error && (
                    <Box sx={{ p: 3 }}>
                        <Alert severity="error">
                            {t('ACCELERATOR_LOAD_ERROR')}
                        </Alert>
                    </Box>
                )}

                {/* Accelerators Grid */}
                {!isLoading && !error && (
                    <Box sx={{ 
                        display: 'grid', 
                        gridTemplateColumns: 'repeat(3, 1fr)', 
                        gap: '20px'
                    }}>
                        {filteredAccelerators.map((accelerator) => (
                            <Box key={accelerator.name}>
                                <Card
                                sx={{
                                    width: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    border: '1px solid #ddd',
                                    borderRadius: '4px',
                                    boxShadow: 'none',
                                    transition: 'box-shadow 0.2s',
                                    '&:hover': {
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                                    },
                                }}
                            >
                                {/* Logo/Image */}
                                <Box
                                    sx={{
                                        height: 160,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        backgroundColor: '#ffffff',
                                        borderBottom: '1px solid #ddd',
                                        p: 3,
                                    }}
                                >
                                    <CardMedia
                                        component="img"
                                        image={accelerator.imageURL}
                                        alt={accelerator.name}
                                        sx={{
                                            maxHeight: '100%',
                                            maxWidth: '100%',
                                            objectFit: 'contain',
                                        }}
                                    />
                                </Box>

                                <CardContent sx={{ flexGrow: 1, p: 2.5 }}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5, fontSize: '1.1rem', color: '#333' }}>
                                        {accelerator.name}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6, fontSize: '0.875rem' }}>
                                        {accelerator.description}
                                    </Typography>
                                </CardContent>

                                {/* Actions */}
                                <CardActions sx={{ p: 2.5, pt: 0, gap: 1.5, display: 'flex' }}>
                                    <Button
                                        variant="contained"
                                        fullWidth
                                        onClick={() => handleInstall(accelerator)}
                                        disabled={installing === accelerator.name || isInstallingWizard}
                                        sx={{
                                            textTransform: 'none',
                                            fontWeight: 600,
                                            backgroundColor: '#666',
                                            color: 'white',
                                            '&:hover': {
                                                backgroundColor: '#555',
                                            },
                                            '&.Mui-disabled': {
                                                backgroundColor: '#999',
                                                color: 'white',
                                            },
                                        }}
                                    >
                                        {installing === accelerator.name ? t('ACCELERATOR_INSTALLING') : t('ACCELERATOR_BTN_INSTALL')}
                                    </Button>
                                    <Button
                                        variant="contained"
                                        fullWidth
                                        onClick={() => handleLearnMore(accelerator)}
                                        disabled={isInstallingWizard}
                                        sx={{
                                            textTransform: 'none',
                                            fontWeight: 600,
                                            backgroundColor: '#ff9800',
                                            color: 'white',
                                            '&:hover': {
                                                backgroundColor: '#f57c00',
                                            },
                                        }}
                                    >
                                        {t('ACCELERATOR_BTN_LEARN_MORE')}
                                    </Button>
                                </CardActions>
                            </Card>
                        </Box>
                    ))}
                </Box>
                )}

                {/* No Results */}
                {!isLoading && !error && filteredAccelerators.length === 0 && (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: '100%',
                            py: 8,
                        }}
                    >
                        <SearchIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                        <Typography variant="h6" color="text.secondary">
                            {t('ACCELERATOR_NO_FOUND')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {t('ACCELERATOR_TRY_ADJUST_SEARCH')}
                        </Typography>
                    </Box>
                )}
            </Box>

            {/* Footer Section with Help Button */}
            <Box sx={{ p: 2, borderTop: '1px solid #e0e0e0', backgroundColor: '#f9f9f9' }}>
                <Button
                    variant="outlined"
                    startIcon={<HelpOutlineIcon />}
                    onClick={handleOpenHelp}
                    disabled={isInstallingWizard}
                    sx={{
                        textTransform: 'none',
                        
                    }}
                >
                    {t('BTN_HELP')}
                </Button>
            </Box>

            {/* Configuration Confirmation Dialog */}
            <Dialog
                open={showConfigDialog}
                onClose={handleSkipConfig}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: '8px',
                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
                        overflow: 'hidden'
                    }
                }}
            >
                <DialogTitle
                    component="div"
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        py: 2.5,
                        px: 3.5,
                        backgroundColor: '#F8F8F8',
                        borderBottom: '1px solid #e8e8e8',
                        '&.MuiDialogTitle-root': { flex: 1 }
                    }}
                >
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#1a1a1a', pr: 2 }}>
                        {t('ACCELERATOR_PROCEED_CONFIG_TIT')}
                    </Typography>
                    <IconButton
                        onClick={handleSkipConfig}
                        size="small"
                        sx={{
                            flexShrink: 0,
                            '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.06)' }
                        }}
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </DialogTitle>
                <DialogContent
                    sx={{
                        marginTop: '28px !important',
                        pt: 4.5,
                        pb: 3.5,
                        px: 3.5,
                        backgroundColor: '#ffffff'
                    }}
                >
                    <Box sx={{ display: 'flex', gap: 2.5, alignItems: 'center' }}>
                        <Box
                            sx={{
                                flexShrink: 0,
                                backgroundColor: '#1976d2',
                                borderRadius: '50%',
                                width: 48,
                                height: 48,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                        >
                            <AutoAwesomeIcon sx={{ color: 'white', fontSize: 26 }} />
                        </Box>
                        <Typography variant="body1" sx={{ color: '#1a1a1a', lineHeight: 1.5 }}>
                            {t('ACCELERATOR_PROCEED_CONFIG_MSG')}
                        </Typography>
                    </Box>
                </DialogContent>
                <DialogActions
                    sx={{
                        py: 2.5,
                        px: 3.5,
                        pt: 2,
                        backgroundColor: '#F8F8F8',
                        borderTop: '1px solid #e8e8e8',
                        justifyContent: 'flex-end',
                        gap: 2
                    }}
                >
                    <Button
                        onClick={handleProceedWithConfig}
                        variant="outlined"
                        sx={{
                            textTransform: 'none',
                            fontWeight: 600,
                            px: 3,
                            py: 1.25,
                            borderRadius: '6px',
                            backgroundColor: 'white',
                            color: '#1a1a1a',
                            borderColor: '#d0d0d0',
                            '&:hover': {
                                backgroundColor: '#f5f5f5',
                                borderColor: '#b0b0b0'
                            }
                        }}
                    >
                        {t('ACCELERATOR_BTN_YES_PROCEED')}
                    </Button>
                    <Button
                        onClick={handleSkipConfig}
                        variant="contained"
                        sx={{
                            textTransform: 'none',
                            fontWeight: 600,
                            px: 3,
                            py: 1.25,
                            borderRadius: '6px',
                            backgroundColor: '#555',
                            color: 'white',
                            '&:hover': { backgroundColor: '#444' }
                        }}
                    >
                        {t('ACCELERATOR_BTN_SKIP_LATER')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Accelerator Workflow Wizard */}
            <AcceleratorWorkflowWizard 
                open={showWorkflowWizard}
                onClose={handleWorkflowWizardClose}
            />
        </Box>
    );
};

export default AcceleratorWizard;
